import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Tooltip } from 'reactstrap';
import FeedbackMessage from './FeedbackMessage';
import * as url from '../../urls/api-users/merchant';
import * as ai_urls from '../../urls/api-ai/merchant';
import { calculateTotalWeight } from '../SharedFunctions/weightCalculations';

export const BROKER_NOTES_HEADER = 'Gophr Broker notes:';
const MERCHANT_WEIGHT_SELECTIONS = [50, 100, 200, 300, 400, 500, 1000, 1500, 2500, 3500, 4500, 6250, 8750, 11250, 13750, 16250, 18750, 20000];

const loadingMessages = [
    "Click to skip the guesswork — we'll fill in the weight, size, and best ride. No tape measure needed...",
    'If this takes too long, we blame the interns...',
    'Delivering code like we deliver freight: fast, heavy, and slightly sweaty...',
    'Doing math so you do not have to...',
    'Consulting the logistics gods...',
    'Faking intelligence please wait...',
    '99.7% sure it will be right this time...',
    'Hold up we will be finished before the construction on I-10...',
    'Crunching the numbers...',
    'Working up your quote...',
    'Getting the details just right...',
    'Almost done! Like my gym membership...',
    'Turning caffeine into quotes...',
    'Thinking… but like, fast...',
    'Loading… still faster than a warehouse inventory count...',
    'Just a few billion calculations to go...',
    'Loading… unlike that dock guy on break for 47 minutes...',
    'Math is hard. Give me a sec...',
    'This better be right...',
    'Trying not to break anything...',
    "We'll get there… eventually...",
    "Trust me, I'm a robot...",
    'Boom. Math incoming...',
    'Watch this magic...',
    'Here comes your quote...',
    "Let's make this make sense...",
    'Quote is en route...',
    "We're not lost. We're 'creatively rerouted.'...",
    "Bracing for another call that starts with 'Where's my stuff?'...",
    "Unleashing the tiny digital gophrs — they're very motivated...",
    "Hang tight — we're sizing up your load like a forklift on payday...",
    "Running the numbers… and by 'running' we mean 'riding a pallet jack uphill.'...",
    'Estimating faster than a foreman late for lunch...',
    "Don't worry, we asked the forklift psychic...",
    'Calculating with all the grace of a backhoe ballet...',
    'Fitting this puzzle together like a pro — no duct tape required...',
    'Finding the right truck… Not too big, not too small. Goldilocks would be proud...',
    "Sweating over this quote like it's a Louisiana swamp...",
    'Math is being done. There will be no pop quiz...',
    'Just a moment — arguing with a semi about its max payload...',
    "Weight, weight, don't tell me...",
    'Rolling tape, checking crates, channeling greatness...',
    'Still faster than Steve from receiving...',
    'Trust the process — it includes coffee and mild panic...',
    'Packing peanuts is not included in this calculation...',
    "Cross-referencing Newton's laws and your budget...",
    "It's not AI, it's just Carl in Dispatch with a calculator. Kidding. Kinda...",
    "Running faster than your buddy who 'forgot' his steel-toes...",
    "One moment while we measure this like it's going on the Space Shuttle...",
    "We promise this is smarter than your cousin who said 'just throw it in the trunk.'...",
    "Calculating… somewhere between 'brb' and 'hold my beer.'...",
    'Making sure this fits better than your last load… you know the one...',
    'Math in progress. Forklifts standing by...',
    'Quote coming in hotter than a welder in July...',
    'Sizing things up like a grizzled warehouse vet with laser eyes...',
    "We're not stalling, we're calibrating...",
    'Still faster than Chuck trying to back in a trailer...',
    "Running numbers like it's shift change at the plant...",
    'Your quote is almost here… unlike that one part from Tulsa...',
    "This one's got more calculations than your uncle's fish stories...",
    'Cranking out results with union-approved precision...',
    'Loading brainpower… may require more caffeine...',
    'This quote is handcrafted, artisan-style...',
    "Making load plans, checking 'em twice...",
    'Optimizing like a pro, minus the clipboard...',
    "We're doing math. Don't tell the crew...",
    "Tetris-ing your freight like it's 1992...",
    'Rechecking your specs… and our sanity...',
    "It's almost done — don't make us turn this truck around...",
    'Just a sec — arguing with an algorithm over axle weight...',
    'Still faster than our last company-wide email reply...',
    'Quote in progress… unless Carl unplugged the server again...',
    'Doing some heavy lifting (with math, not muscles)...',
    "If this takes forever, blame the guy who said, 'How hard can it be?'...",
    "Putting the 'fun' in 'fundamental logistics failure.'...",
    "Estimating like it's our first day. Because it might be...",
    "One moment — just arguing over what a 'medium pallet' means...",
    'Running numbers with the confidence of a guy who lost a wrench in a crate...',
    'Quote is calculating. So are our life choices...',
    'Still more accurate than our office dartboard delivery estimator...',
    'Trying to impress you with logistics math. Please applaud...',
    "Fetching data. Probably from a dusty Excel sheet in someone's inbox...",
    "We swear there's science behind this. Mostly caffeine and fear, though...",
    "Rebooting our 'magic box' — again...",
    'Filling this out like a forklift certification test: half guesswork, full send...',
    "It's math time. Please stand by while we pretend to enjoy it...",
    "If it helps, we're confused too...",
    'Winging it… but like, with spreadsheets...',
    "We've almost cracked it. Don't ask how...",
    'Working on it… slowly, like a Monday morning...',
    "Still better than asking 'Hey, what's the weight of that thing?'...",
    'This quote was brought to you by duct tape and crossed fingers...',
    "Wait for it… or don't. We're still going to finish it anyway...",
    'Actively pretending this is part of the plan...',
    "If this fails, we'll just blame AI. Again...",
    "Hang on — we're Googling 'How to measure a giant weird-shaped box.'..."
];

const VEHICLE_TYPE_ALIASES = {
    car: 'car',
    suv: 'suv',
    truck: 'truck',
    pickup: 'truck',
    pickup_truck: 'truck',
    heavy_duty_truck: 'heavy_duty_truck',
    one_ton_pickup: 'heavy_duty_truck',
    sprinter_van: 'sprinter_van',
    trailer: 'trailer',
    bumper_pull: 'trailer',
    bumper_pull_trailer: 'trailer',
    box_truck: 'box_truck',
    car_hauler: 'car_hauler',
    gooseneck: 'gooseneck_trailer',
    gooseneck_trailer: 'gooseneck_trailer',
    flatbed: 'trailer',
    dry_van: '18_wheeler_dry_van',
    reefer: '18_wheeler_reefer',
    '18_wheeler_flatbed': '18_wheeler_flat_bed',
    '18_wheeler_flat_bed': '18_wheeler_flat_bed',
    '18_wheeler_dryvan': '18_wheeler_dry_van',
    '18_wheeler_dry_van': '18_wheeler_dry_van',
    '18_wheeler_reefer': '18_wheeler_reefer'
};

const normalizeBrokerVehicleType = vehicleRecommendation => {
    const normalized = (vehicleRecommendation || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');

    if (normalized.includes('18') || normalized.includes('semi') || normalized.includes('tractor')) {
        if (normalized.includes('flat') || normalized.includes('stake')) {
            return '18_wheeler_flat_bed';
        }
        if (normalized.includes('reefer')) {
            return '18_wheeler_reefer';
        }
        if (normalized.includes('dry')) {
            return '18_wheeler_dry_van';
        }
    }

    if (normalized.includes('stakebed') || normalized.includes('stake_bed')) {
        return 'trailer';
    }

    if (normalized.includes('bumper_pull') || normalized.includes('bumperpull')) {
        return 'trailer';
    }

    if (normalized.includes('flatbed') || normalized.includes('flat_bed')) {
        return normalized.includes('gooseneck') ? 'gooseneck_trailer' : 'trailer';
    }

    if (normalized.includes('straight_truck') || normalized.includes('straighttruck')) {
        return 'box_truck';
    }

    if (normalized.includes('sprinter')) {
        return 'sprinter_van';
    }

    if (normalized.includes('gooseneck')) {
        return 'gooseneck_trailer';
    }

    if (normalized.includes('box_truck') || normalized.includes('boxtruck')) {
        return 'box_truck';
    }

    if (normalized.includes('heavy_duty') || normalized.includes('heavyduty') || normalized.includes('one_ton') || normalized.includes('1_ton')) {
        return 'heavy_duty_truck';
    }

    if (normalized.includes('pickup') || normalized.includes('half_ton') || normalized.includes('1_2_ton')) {
        return 'truck';
    }

    return VEHICLE_TYPE_ALIASES[normalized] || '';
};

const BROKER_TOOL_ALIASES = {
    handloading: 'ishandloading',
    handload: 'ishandloading',
    manualloading: 'ishandloading',
    dolly: 'isdolly',
    palletjack: 'ispalletjack',
    pallettruck: 'ispalletjack',
    liftgate: 'isliftgate',
    hazmat: 'ishazmat',
    twic: 'istwiccard',
    twiccard: 'istwiccard',
    weatherprotection: 'isweatherprotection',
    medical: 'ismedical',
    personalprotectiveequipment: 'isppe',
    protectiveequipment: 'isppe',
    ppe: 'isppe'
};

const normalizeBrokerToolToken = value => {
    const normalized = (value || '').toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '');

    if (!normalized) {
        return null;
    }

    return BROKER_TOOL_ALIASES[normalized] || normalized;
};

const normalizeBrokerTools = tools => {
    const normalizedTools = new Set();

    const addToken = value => {
        const token = normalizeBrokerToolToken(value);

        if (token) {
            normalizedTools.add(token);
        }
    };

    const collect = value => {
        if (value == null) {
            return;
        }

        if (Array.isArray(value)) {
            value.forEach(collect);
            return;
        }

        if (typeof value === 'object') {
            ['name', 'label', 'tool', 'type', 'equipment', 'key'].forEach(field => {
                if (value[field]) {
                    addToken(value[field]);
                }
            });

            Object.keys(value).forEach(key => {
                if (value[key] === true) {
                    addToken(key);
                }
            });

            return;
        }

        addToken(value);
    };

    collect(tools);
    return normalizedTools;
};

const parseBrokerWeight = value => {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        return Math.round(value);
    }

    if (typeof value !== 'string') {
        return null;
    }

    const matches = value.match(/\d[\d,]*(?:\.\d+)?/g);
    if (!matches || matches.length === 0) {
        return null;
    }

    const normalizedNumbers = matches
        .map(match => Number.parseFloat(match.replace(/,/g, '')))
        .filter(number => Number.isFinite(number) && number > 0);

    if (normalizedNumbers.length === 0) {
        return null;
    }

    return Math.round(Math.max(...normalizedNumbers));
};

const normalizeBrokerWeightSelection = value => {
    if (!Number.isFinite(value) || value <= 0) {
        return null;
    }

    return MERCHANT_WEIGHT_SELECTIONS.find(selection => value <= selection) || MERCHANT_WEIGHT_SELECTIONS[MERCHANT_WEIGHT_SELECTIONS.length - 1];
};

const getBrokerVehicleRecommendation = brokerPlan =>
    brokerPlan && (
        brokerPlan.vehicleRecommendation
        || brokerPlan.vehicle_recommendation
        || brokerPlan.vehicle_type
        || brokerPlan.recommended_vehicle
        || brokerPlan.recommendedVehicle
    );

const getBrokerWeightRecommendation = brokerPlan =>
    brokerPlan && (
        brokerPlan.total_weight
        || brokerPlan.totalWeight
        || brokerPlan.estimated_weight
        || brokerPlan.estimatedWeight
        || brokerPlan.weight
    );

const getBrokerHelperCount = brokerPlan =>
    brokerPlan && (
        brokerPlan.num_of_gophrs
        || brokerPlan.numOfGophrs
        || brokerPlan.helper_count
        || brokerPlan.helperCount
        || brokerPlan.num_people
        || brokerPlan.numPeople
    );

export const removeExistingBrokerBlock = value => {
    const trimmed = (value || '').trim();
    const markerIndex = trimmed.indexOf(BROKER_NOTES_HEADER);
    return markerIndex === -1 ? trimmed : trimmed.slice(0, markerIndex).trim();
};

// Returns ONLY the "Gophr Broker notes:" block from a stored instruction, or ''.
export const extractBrokerBlock = value => {
    const trimmed = (value || '').trim();
    const markerIndex = trimmed.indexOf(BROKER_NOTES_HEADER);
    return markerIndex === -1 ? '' : trimmed.slice(markerIndex).trim();
};

const splitBrokerBullets = value =>
    (value || '')
        .split(/[\n;]+|(?<=[.!?])\s+/)
        .map(part => part.trim())
        .map(part => part.replace(/^[-*•\s]+/, '').replace(/[.!?]+$/, '').trim())
        .filter(Boolean);

const dedupeBullets = bullets => {
    const seen = new Set();
    return bullets.filter(bullet => {
        const normalized = bullet.toLowerCase();
        if (seen.has(normalized)) {
            return false;
        }
        seen.add(normalized);
        return true;
    });
};

const normalizeInstructionText = value =>
    String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

const titleizeBullet = bullet => (bullet ? bullet.charAt(0).toUpperCase() + bullet.slice(1) : bullet);

const getBrokerInstructionBullets = brokerAssist => {
    const toolBullets = [];
    const normalizedTools = normalizeBrokerTools(brokerAssist && brokerAssist.equipmentAndTools);

    if (normalizedTools.has('ishandloading')) toolBullets.push('Hand load required');
    if (normalizedTools.has('isdolly')) toolBullets.push('Dolly required');
    if (normalizedTools.has('ispalletjack')) toolBullets.push('Pallet jack required');
    if (normalizedTools.has('isliftgate')) toolBullets.push('Lift gate required');
    if (normalizedTools.has('isweatherprotection')) toolBullets.push('Keep load dry');
    if (normalizedTools.has('isforklift')) toolBullets.push('Forklift required');
    if (normalizedTools.has('ishazmat')) toolBullets.push('Hazmat handling required');
    if (normalizedTools.has('istwiccard') || normalizedTools.has('istwic_card')) toolBullets.push('TWIC required');
    if (normalizedTools.has('ismedical')) toolBullets.push('Medical handling required');
    if (normalizedTools.has('isppe')) toolBullets.push('PPE required');

    if (brokerAssist && brokerAssist.estimated_load_minutes) {
        toolBullets.push(`Estimated load time ${brokerAssist.estimated_load_minutes} minutes`);
    }
    if (brokerAssist && brokerAssist.estimated_unload_minutes) {
        toolBullets.push(`Estimated unload time ${brokerAssist.estimated_unload_minutes} minutes`);
    }
    if (brokerAssist && brokerAssist.schedule_reasoning) {
        toolBullets.push(brokerAssist.schedule_reasoning);
    }

    return dedupeBullets([
        ...toolBullets,
        ...splitBrokerBullets(brokerAssist && brokerAssist.special_handling_notes)
    ]).map(titleizeBullet);
};

const getBrokerLocationBullets = (locationKey, brokerAssist) => {
    const location = brokerAssist && brokerAssist[locationKey] ? brokerAssist[locationKey] : null;
    const label = locationKey === 'pickup_location' ? 'Pickup' : 'Dropoff';

    return dedupeBullets([
        ...splitBrokerBullets(location && location.special_instructions),
        ...splitBrokerBullets(location && location.access_notes),
        ...splitBrokerBullets(location && location.operational_notes),
        ...splitBrokerBullets(location && location.site_notes),
        ...splitBrokerBullets(location && location.driver_notes),
        ...splitBrokerBullets(brokerAssist && brokerAssist[`${locationKey === 'pickup_location' ? 'pickup' : 'dropoff'}_instructions`]),
        ...splitBrokerBullets(brokerAssist && brokerAssist[`${locationKey === 'pickup_location' ? 'pickup' : 'dropoff'}_site_orientation`]).map(bullet => `${label} site: ${bullet}`),
        ...splitBrokerBullets(brokerAssist && brokerAssist[`${locationKey === 'pickup_location' ? 'pickup' : 'dropoff'}_hours`]).map(bullet => `${label} hours: ${bullet}`),
    ]).map(titleizeBullet);
};

const mergeBrokerInstructions = ({ existing, locationSpecific, brokerAssist }) => {
    const preservedInstructions = removeExistingBrokerBlock(existing);
    const existingBullets = new Set(splitBrokerBullets(preservedInstructions).map(normalizeInstructionText).filter(Boolean));
    const generatedBullets = dedupeBullets([
        ...splitBrokerBullets(locationSpecific),
        ...getBrokerInstructionBullets(brokerAssist)
    ])
        .filter(bullet => {
            const normalizedBullet = normalizeInstructionText(bullet);
            return normalizedBullet && !existingBullets.has(normalizedBullet);
        })
        .map(titleizeBullet);

    if (!generatedBullets.length) {
        return preservedInstructions;
    }

    const brokerBlock = [BROKER_NOTES_HEADER, ...generatedBullets.map(bullet => `- ${bullet}`)].join('\n');
    return preservedInstructions ? `${preservedInstructions}\n\n${brokerBlock}` : brokerBlock;
};

// Build ONLY the broker-notes block (no customer text) so it can be stored
// separately, hidden during order creation, and appended at submit.
const buildBrokerNotesBlock = ({ existing, locationSpecific, brokerAssist }) => {
    const preservedInstructions = removeExistingBrokerBlock(existing);
    const existingBullets = new Set(splitBrokerBullets(preservedInstructions).map(normalizeInstructionText).filter(Boolean));
    const generatedBullets = dedupeBullets([
        ...splitBrokerBullets(locationSpecific),
        ...getBrokerInstructionBullets(brokerAssist)
    ])
        .filter(bullet => {
            const normalizedBullet = normalizeInstructionText(bullet);
            return normalizedBullet && !existingBullets.has(normalizedBullet);
        })
        .map(titleizeBullet);

    if (!generatedBullets.length) {
        return '';
    }

    return [BROKER_NOTES_HEADER, ...generatedBullets.map(bullet => `- ${bullet}`)].join('\n');
};

// Line-item unit dropdown options. AI item results frequently omit the unit, so
// best-guess it from the item detail text and fall back to 'Units'.
const LINE_ITEM_UNITS = ['Items', 'Units', 'Pallets', 'Boxes', 'Bundles', 'Cartons', 'Crates', 'Drums', 'Rolls', 'Totes'];

const normalizeLineItemUnit = value => {
    if (typeof value !== 'string' || !value.trim()) {
        return '';
    }
    const normalized = value.trim().toLowerCase();
    return LINE_ITEM_UNITS.find(unit => unit.toLowerCase() === normalized) || '';
};

const guessLineItemUnitFromDetail = detail => {
    const text = typeof detail === 'string' ? detail.toLowerCase() : '';
    if (/pallet/.test(text)) return 'Pallets';
    if (/crate/.test(text)) return 'Crates';
    if (/carton/.test(text)) return 'Cartons';
    if (/box/.test(text)) return 'Boxes';
    if (/bundle/.test(text)) return 'Bundles';
    if (/drum/.test(text)) return 'Drums';
    if (/roll/.test(text)) return 'Rolls';
    if (/tote/.test(text)) return 'Totes';
    return 'Units';
};

const resolveLineItemUnit = (existingUnit, aiUnit, detail) =>
    normalizeLineItemUnit(existingUnit) || normalizeLineItemUnit(aiUnit) || guessLineItemUnitFromDetail(detail);

const AIRecommendationButton = ({
    items,
    setItems,
    pickupAddress,
    dropAddress,
    marketId,
    pickupInstruction,
    dropoffInstruction,
    setPickupInstruction,
    setDropoffInstruction,
    setBrokerPickupNotes,
    setBrokerDropoffNotes,
    setVehicleType,
    setWeight,
    setNumPeople,
    setIsHandLoadUnload,
    setIsLiftgate,
    setIsPalletJack,
    setIsDolly,
    setIsHazmat,
    setIsTWICCard,
    setIsWeatherProtection,
    setIsMedical,
    setIsPPE,
    setAIExplanation,
    setShowExplanation,
    setLTLPrice,
    setLTLLeadTime,
    setRecommendedVehicleType,
    onAIRecommendationApplied,
    setRecommendedWeight,
    autoTrigger = false,  // When true, fires automatically on item name change
    autoTriggerCount = 0, // Increment from parent to fire programmatically
}) => {
    const [message, setMessage] = useState('');
    const [isClickDisabled, setIsClickDisabled] = useState(true);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [isAutoFetching, setIsAutoFetching] = useState(false);
    const typingIntervalRef = useRef(null);
    const typingCompleteRef = useRef(true); // true when no typing animation in progress
    const pendingDoneMessageRef = useRef(false); // set true when AI done but typing still going
    const autoDebounceRef = useRef(null);
    const lastAutoTriggeredItem = useRef('');
    const autoFetchingRef = useRef(false);
    const prevAutoTriggerCount = useRef(0); // ref version — never stale in effects

    const toggleTooltip = () => setTooltipOpen(!tooltipOpen);

    //TODO: Some conflicts between old rules and new ai generated results, eg. weight and vehicleType restrictions
    const toolSetters = {
        isHandLoadUnload: setIsHandLoadUnload,
        isLiftgate: setIsLiftgate,
        isPalletJack: setIsPalletJack,
        isDolly: setIsDolly,
        isHazmat: setIsHazmat,
        isTWICCard: setIsTWICCard,
        isWeatherProtection: setIsWeatherProtection,
        isMedical: setIsMedical,
        isPPE: setIsPPE
    };

    useEffect(() => {
        if (items.length > 0 && items.every(item => item.detail && item.detail.trim() !== '' && item.unit && item.unit.trim() !== '') && pickupAddress && dropAddress) {
            setIsClickDisabled(false);
        } else {
            setIsClickDisabled(true);
        }
    }, [items, pickupAddress, dropAddress]);

    const typeMessage = fullMessage => {
        const words = fullMessage.split(' ');
        let currentIndex = 0;

        setMessage('');
        typingCompleteRef.current = false;
        pendingDoneMessageRef.current = false;

        typingIntervalRef.current = setInterval(() => {
            if (currentIndex < words.length) {
                setMessage(prev => prev + (currentIndex === 0 ? '' : ' ') + words[currentIndex]);
                currentIndex++;
            } else {
                clearInterval(typingIntervalRef.current);
                typingCompleteRef.current = true;
                // If AI already finished while we were typing, show Done! now
                if (pendingDoneMessageRef.current) {
                    pendingDoneMessageRef.current = false;
                    setMessage('Done! Review the info below and make any changes needed.');
                }
            }
        }, 400);
    };

    useEffect(() => {
        return () => {
            if (typingIntervalRef.current) {
                clearInterval(typingIntervalRef.current);
            }
        };
    }, []);

    const buildBrokerPrompt = () => {
        const itemLines = (Array.isArray(items) ? items : [])
            .map((item, index) => {
                const quantity = item && item.quantity ? item.quantity : 0;
                const detail = item && item.detail ? item.detail : 'item';
                const unit = item && item.unit ? item.unit : 'unit';
                return `${index + 1}. ${quantity} x ${detail} (${unit})`;
            })
            .join('\n');

        return `Pickup:\n${pickupAddress || ''}\n${pickupInstruction ? `Instructions: ${pickupInstruction}` : ''}\n\nDropoff:\n${dropAddress || ''}\n${dropoffInstruction ? `Instructions: ${dropoffInstruction}` : ''}\n\nItems:\n${itemLines || '- No items provided'}`;
    };

    const applyBrokerAssistToForm = brokerPlan => {
        if (!brokerPlan || typeof brokerPlan !== 'object') {
            return;
        }

        const normalizedTools = normalizeBrokerTools(brokerPlan.equipmentAndTools);
        const nextVehicleType = normalizeBrokerVehicleType(getBrokerVehicleRecommendation(brokerPlan));
        const parsedWeight = parseBrokerWeight(getBrokerWeightRecommendation(brokerPlan));
        const nextWeight = normalizeBrokerWeightSelection(parsedWeight);
        const nextHelperCount = parseInt(getBrokerHelperCount(brokerPlan), 10);

        if (Number.isFinite(nextWeight) && nextWeight > 0) {
            setWeight(nextWeight);
        }

        if (Number.isFinite(parsedWeight) && parsedWeight > 0) {
            setRecommendedWeight(parsedWeight);
        }

        if (nextVehicleType) {
            setVehicleType(nextVehicleType);
            setRecommendedVehicleType(nextVehicleType);
        }

        if (!Number.isNaN(nextHelperCount) && nextHelperCount > 0) {
            setNumPeople(nextHelperCount);
        }

        setIsHandLoadUnload(normalizedTools.has('ishandloading'));
        setIsLiftgate(normalizedTools.has('isliftgate'));
        setIsPalletJack(normalizedTools.has('ispalletjack'));
        setIsDolly(normalizedTools.has('isdolly'));
        setIsHazmat(normalizedTools.has('ishazmat'));
        setIsTWICCard(normalizedTools.has('istwiccard') || normalizedTools.has('istwic_card'));
        setIsWeatherProtection(normalizedTools.has('isweatherprotection'));
        setIsMedical(normalizedTools.has('ismedical'));
        setIsPPE(normalizedTools.has('isppe'));

        if (brokerPlan.customerExplanation) {
            setAIExplanation(brokerPlan.customerExplanation);
            setShowExplanation(true);
        }

        // Keep broker notes separate from the customer's visible notes: when the
        // parent provides a broker-notes setter, store the block there (hidden in
        // the form, merged at submit); otherwise fall back to the inline merge.
        if (typeof setBrokerPickupNotes === 'function') {
            setBrokerPickupNotes(
                buildBrokerNotesBlock({
                    existing: pickupInstruction,
                    locationSpecific: getBrokerLocationBullets('pickup_location', brokerPlan).join('\n'),
                    brokerAssist: brokerPlan
                })
            );
        } else if (typeof setPickupInstruction === 'function') {
            setPickupInstruction(
                mergeBrokerInstructions({
                    existing: pickupInstruction,
                    locationSpecific: getBrokerLocationBullets('pickup_location', brokerPlan).join('\n'),
                    brokerAssist: brokerPlan
                })
            );
        }

        if (typeof setBrokerDropoffNotes === 'function') {
            setBrokerDropoffNotes(
                buildBrokerNotesBlock({
                    existing: dropoffInstruction,
                    locationSpecific: getBrokerLocationBullets('dropoff_location', brokerPlan).join('\n'),
                    brokerAssist: brokerPlan
                })
            );
        } else if (typeof setDropoffInstruction === 'function') {
            setDropoffInstruction(
                mergeBrokerInstructions({
                    existing: dropoffInstruction,
                    locationSpecific: getBrokerLocationBullets('dropoff_location', brokerPlan).join('\n'),
                    brokerAssist: brokerPlan
                })
            );
        }
    };

    const applyLegacyRecommendation = data => {
        if (!data || data.error) {
            return false;
        }

        setWeight(calculateTotalWeight(data.totalWeight));
        setRecommendedWeight(data.totalWeight);
        const vehicleType = normalizeBrokerVehicleType(data.vehicleRecommendation);
        if (!vehicleType) {
            return false;
        }
        setVehicleType(vehicleType);
        setRecommendedVehicleType(vehicleType);

        setItems(prevItems =>
            data.items.map((newItem, index) => ({
                ...prevItems[index],
                ...newItem
            }))
        );
        setNumPeople(1);
        if (Array.isArray(data.equipmentAndTools) && data.equipmentAndTools.length > 0) {
            Object.entries(toolSetters).forEach(([tool, setter]) => {
                const isIncluded = data.equipmentAndTools.includes(tool);
                setter(isIncluded);
            });
        }
        if (data.customerExplanation) {
            setAIExplanation(data.customerExplanation);
            setShowExplanation(true);
        }
        setLTLPrice(data.LTLRate);
        setLTLLeadTime(data.LTLLeadTime);

        return true;
    };

    const makeChatGptCall = () => {
        // Hard re-entrancy guard — only one call at a time
        if (autoFetchingRef.current) return;
        autoFetchingRef.current = true;
        setIsAutoFetching(true);
        setIsClickDisabled(true);
        const randomMessage = loadingMessages[Math.floor(Math.random() * loadingMessages.length)];
        typeMessage(randomMessage);

        // Always use production URLs for AI calls — local api-users/api-ai are not run locally
        const usersAiUrl = 'https://api-users.gophr.app/shipment/chatgpt/assistant';
        const brokerAiUrl = 'https://api-ai.gophr.app/gophr-broker/text-only-chat';
        const authHeader = 'Bearer ' + window.localStorage.getItem('access_token');
        // API requires unit to be non-empty — default to 'each' if not yet selected
        const itemsWithUnit = (items || []).map(item => ({
            ...item,
            unit: (item.unit && item.unit.trim()) ? item.unit : 'each',
        }));

        Promise.allSettled([
            axios({
                method: 'post',
                url: usersAiUrl,
                withCredentials: true,
                headers: { 'content-type': 'application/json', Authorization: authHeader },
                data: { items: itemsWithUnit, pickupAddress, dropAddress, do_tql_cal: true }
            }),
            axios({
                method: 'post',
                url: brokerAiUrl,
                withCredentials: true,
                headers: { 'content-type': 'application/json', Authorization: authHeader },
                data: { text: buildBrokerPrompt(), marketId: marketId || null }
            })
        ])
            .then(([legacyResult, brokerResult]) => {
                let hasAnySuccess = false;

                if (legacyResult.status === 'fulfilled' && legacyResult.value.status === 200 && legacyResult.value.data.status === 'successful') {
                    hasAnySuccess = applyLegacyRecommendation(legacyResult.value.data.payload) || hasAnySuccess;
                }

                if (brokerResult.status === 'fulfilled' && brokerResult.value.status === 200 && brokerResult.value.data) {
                    applyBrokerAssistToForm(brokerResult.value.data);
                    hasAnySuccess = true;
                }

                if (hasAnySuccess) {
                    // After AI runs, guarantee every line item has a unit — respect an AI-provided
                    // unit, otherwise best-guess from the detail or default to 'Units'.
                    setItems(prevItems => (prevItems || []).map(item => ({
                        ...item,
                        unit: resolveLineItemUnit(item.unit, '', item.detail),
                    })));
                    if (typingCompleteRef.current) {
                        // Typing already done — show Done! immediately
                        setMessage('Done! Review the info below and make any changes needed.');
                    } else {
                        // Still typing — queue Done! to appear when typing finishes
                        pendingDoneMessageRef.current = true;
                    }
                    if (onAIRecommendationApplied) onAIRecommendationApplied();
                    return;
                }

                setMessage('Something Wrong with AI, please try again or manually edit');
                setIsClickDisabled(false);
                autoFetchingRef.current = false;
                setIsAutoFetching(false);
            })
            .catch(() => {
                setIsClickDisabled(false);
                autoFetchingRef.current = false;
                setIsAutoFetching(false);
                setMessage('Something Wrong, please try again or manually edit');
            });
    };

    // Programmatic trigger: parent increments autoTriggerCount → fires makeChatGptCall() directly
    useEffect(() => {
        if (autoTriggerCount > 0 && autoTriggerCount !== prevAutoTriggerCount.current) {
            prevAutoTriggerCount.current = autoTriggerCount;
            makeChatGptCall();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoTriggerCount]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <div id="aiRecommendationWrapper" style={{ display: 'inline-block' }}>
                <Button
                    className="btn submit-btn"
                    color={isClickDisabled ? 'light' : 'success'}
                    onClick={() => {
                        makeChatGptCall();
                    }}
                    disabled={isClickDisabled}
                >
                    <span
                        style={{
                            marginLeft: 4,
                            display: 'inline-block',
                            color: 'white',
                            fontSize: '1.5em',
                            lineHeight: 1,
                            filter: 'brightness(0) invert(1)'
                        }}
                        role="img"
                        aria-label="sparkles"
                    >
                        ✨
                    </span>
                    {'  '}AI ASSIST
                </Button>
            </div>
            <Tooltip isOpen={tooltipOpen} toggle={toggleTooltip} target="aiRecommendationWrapper" placement="top">
                <div
                    style={{
                        background: '#fff',
                        color: '#222',
                        borderRadius: 8,
                        padding: '16px 18px',
                        fontSize: 13,
                        lineHeight: 1.6,
                        minWidth: 350,
                        width: 350,
                        maxWidth: 350,
                        border: '1.5px solid #b5d2ee',
                        textAlign: 'left',
                        boxShadow: 'none'
                    }}
                >
                    Click to let Gophr suggest delivery options based on your shipment. Our AI isn’t perfect — review everything before you roll.
                </div>
            </Tooltip>
            <FeedbackMessage message={message} showSpinner={message.includes('...')} />
        </div>
    );
};

export default AIRecommendationButton;
